import { useEffect, useState } from "react";
import { MDBBtn, MDBContainer } from "mdb-react-ui-kit";

import { useAuth } from "../../components/Auth";
import { CountCard } from "../../components";
import ErrorMessage from "../../components/ErrorMessage/ErrorMessage";
import { FilterIconHeader } from "../../components/FilterIconHeader";
import { SwitchForm } from "../../components/SwitchForm";
import {
  formatScreeningOutcomeHits,
  formatScreeningOutcomeHitsPlainText,
  SignalCheckReviewModal,
} from "./components";

import { getDataTableColumns } from "./columns";
import {
  convertISOToReadableDate,
  showErrorToast,
  extractUniqueScreeningOutcomeTypes,
} from "../../helpers";
import { handleSetSubmissionStatus } from "../../helpers/statusHelpers";
import { getSignalCheckReports } from "../../helpers/signalCheck";
import { exportSignalCheckToExcel } from "./export";

import { signalCheckSignals } from "../../constants";
import { DataTable } from "stm-frontend-library";
import { getSelectedJournalIds } from "../../helpers/journals";
import ConfirmationDialog from "../../components/ConfirmationDialog/ConfirmationDialog";

const ACTIONS_REQUIRING_CONFIRMATION = ["suspect paper mill"];

const SignalCheck = () => {
  const { profile } = useAuth();

  const [error, setError] = useState(false);

  const [allData, setAllData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [totalHits, setTotalHits] = useState(0);
  const [filterSelection, setFilterSelection] = useState([]);
  const [selectedDataItem, setSelectedDataItem] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  const [confirmationDialog, setConfirmationDialog] = useState({
    isOpen: false,
    actionType: null,
  });

  const handleRowClick = (index) => {
    setSelectedDataItem(filteredData[index]);
  };

  const handleCloseModal = () => {
    setSelectedDataItem(null);
  };

  const handleActionSelect = async (_, status, additionalParams = {}) => {
    if (ACTIONS_REQUIRING_CONFIRMATION.includes(status)) {
      setConfirmationDialog({
        isOpen: true,
        actionType: status,
      });
      return;
    }

    await processAction(status, additionalParams);
  };

  const processAction = async (status, additionalParams = {}) => {
    if (!selectedDataItem?.id) {
      return;
    }
    try {
      const fullParams = {
        persistentId: selectedDataItem.persistent_id,
        ...(additionalParams.addToSignalcheck && {
          signalCheck: {
            user: {
              comment: additionalParams.comment || "",
            },
            manuscript: {
              title: selectedDataItem.title,
              manuscript_id: selectedDataItem.manuscript_id,
              publisher_id: selectedDataItem.publisher_id,
              user_id: selectedDataItem.user_id,
            },
          },
        }),
      };
      await handleSetSubmissionStatus(selectedDataItem.id, status, fullParams);
      const updatedData = allData.map(item =>
        item.id === selectedDataItem.id
          ? { ...item, submission_status: status }
          : item
      );
      setAllData(updatedData);
      setSelectedDataItem(prev => ({ ...prev, submission_status: status }));
    } catch (err) {
      console.error('handleActionSelect error:', err);
      showErrorToast();
    }
  };

  const handleActionUndo = async () => {
    await processAction(null, {});
  };

  const handleConfirmationDialogClose = () => {
    setConfirmationDialog({
      isOpen: false,
      actionType: null,
    });
  };

  const handleConfirmationDialogApply = async (additionalParams) => {
    const { actionType } = confirmationDialog;
    handleConfirmationDialogClose();
    await processAction(actionType, additionalParams);
  };

  const dataWithActions = filteredData?.map((element, index) => ({
    ...element,
    timestamp: convertISOToReadableDate(element.timestamp, "DD MMM YYYY"),
    raw_hits: formatScreeningOutcomeHitsPlainText(element.screening_outcomes),
    hits: (
      <span
        dangerouslySetInnerHTML={{
          __html: formatScreeningOutcomeHits(element.screening_outcomes),
        }}
      />
    ),
    manuscript_id: element.manuscript_id ?? "",
    clickEvent: () => handleRowClick(index),
  }));

  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      try {
        const selectedJournalIds = await getSelectedJournalIds();
        const { data: { manuscripts = [], total = 0 } = {} } =
          await getSignalCheckReports(selectedJournalIds);
        setAllData(manuscripts);
        setTotalHits(total);
        // Initialize filter with ALL enabled SignalCheck signals, but only pre-select those with hits
        const enabledSignalCheckSignals = signalCheckSignals.filter(
          (signal) => profile[`tool.${signal.value}`]
        );

        // Get signals that actually have hits in the data
        const signalsWithHits = extractUniqueScreeningOutcomeTypes(manuscripts);

        const initialFilter = Object.fromEntries(
          enabledSignalCheckSignals.map((signal) => [
            signal.value,
            signalsWithHits.includes(signal.value), // Only true if signal has hits
          ])
        );
        setFilterSelection(initialFilter);
      } catch (error) {
        showErrorToast();
        setError(true);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, []);

  useEffect(() => {
    if (Object.keys(filterSelection).length) {
      const filteredManuscripts = allData.filter((manuscript) =>
        manuscript.screening_outcomes.some(
          (outcome) => filterSelection[outcome.type]
        )
      );
      setFilteredData(filteredManuscripts);
    } else {
      setFilteredData(allData);
    }
  }, [filterSelection, allData]);

  // Create options that include ALL enabled signals from profile, not just signals with hits
  const options = signalCheckSignals
    .filter((signal) => profile[`tool.${signal.value}`])
    .map((signal) => ({
      ...signal,
      value: signal.value.replace("signalCheck.", ""), // Remove the signalCheck. prefix
    }));

  const handleFilterSubmit = (values) => {
    // Add the signalCheck. prefix back to the values
    const valuesWithPrefix = {};
    Object.keys(values).forEach((key) => {
      valuesWithPrefix[`signalCheck.${key}`] = values[key];
    });

    setFilterSelection(valuesWithPrefix);
  };

  const handleFilterReset = () => {
    const resetValues = Object.fromEntries(
      Object.keys(filterSelection).map((type) => [type, true])
    );
    setFilterSelection(resetValues);
  };

  // Create initial values without the signalCheck. prefix for the form
  const formInitialValues = {};
  Object.keys(filterSelection).forEach((key) => {
    if (key.startsWith("signalCheck.")) {
      formInitialValues[key.replace("signalCheck.", "")] = filterSelection[key];
    }
  });

  const filterComponent = (
    <FilterIconHeader label="Hits" dropdownTitle="Signal Type Selection">
      <SwitchForm
        handleSubmit={handleFilterSubmit}
        handleReset={handleFilterReset}
        initialValues={formInitialValues}
        options={options}
      />
    </FilterIconHeader>
  );
  const tableColumns = getDataTableColumns(filterComponent);

  const handleExport = () => {
    exportSignalCheckToExcel(dataWithActions, "SignalCheck");
  };

  return (
    <MDBContainer className="my-5">
      {selectedDataItem && (
        <SignalCheckReviewModal
          contentData={selectedDataItem}
          onClose={handleCloseModal}
          onActionSelect={handleActionSelect}
          onActionUndo={handleActionUndo}
        />
      )}
      <ConfirmationDialog
        actionType={confirmationDialog.actionType}
        isOpen={confirmationDialog.isOpen}
        onClose={handleConfirmationDialogClose}
        onApply={handleConfirmationDialogApply}
      />
      {error && <ErrorMessage />}
      {!error && (
        <>
          <div className="d-flex justify-content-center m-5">
            <CountCard
              count={totalHits}
              title={"SIGNALCHECK HITS"}
              subtitle={"Screened manuscripts with hits"}
            />
          </div>
          <div className="d-flex justify-content-end mb-3">
            <MDBBtn
              disabled={isLoading}
              color="primary"
              className="px-4"
              onClick={handleExport}
            >
              Export
            </MDBBtn>
          </div>
          <DataTable
            columns={tableColumns}
            rows={dataWithActions}
            isLoading={isLoading}
          />
        </>
      )}
    </MDBContainer>
  );
};

export default SignalCheck;
